import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { BADGES, COACH_BADGES, getFighterPoints, onCompetitionResult } from '../lib/points'
import { useAuth } from '../hooks/useAuth'
import { getBelt, getBeltTheme } from '../lib/helpers'
import { KIDS_BELTS, ADULT_BELTS, COUNTRIES, calcBeltLevel, calcLevelProgress } from '../lib/constants'
import FighterCard from '../components/FighterCard'
import PhotoUpload from '../components/PhotoUpload'
import BeltPromotion from '../components/BeltPromotion'
import ShareCard from '../components/ShareCard'

const MEMBERSHIP_STATUS_STYLES = {
  active:    { label: 'Active',    bg: '#e6f9ee', color: '#1a7040' },
  overdue:   { label: 'Overdue',   bg: '#fff4e0', color: '#b45000' },
  frozen:    { label: 'Frozen',    bg: '#e8f0ff', color: '#3050a8' },
  expired:   { label: 'Expired',   bg: '#fff0f0', color: '#c00' },
  cancelled: { label: 'Cancelled', bg: '#f5f5f5', color: '#888' },
  none:      { label: 'No plan',   bg: '#f0f0f0', color: '#aaa' },
}

function MembershipBadge({ status }) {
  const s = MEMBERSHIP_STATUS_STYLES[status] || MEMBERSHIP_STATUS_STYLES.none
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: s.bg, color: s.color, fontWeight: 500, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  )
}

export default function CardsPage() {
  const { club } = useAuth()
  const [fighters, setFighters] = useState([])
  const [skills, setSkills] = useState([])
  const [competitions, setCompetitions] = useState([])
  const [categories, setCategories] = useState([])
  const [techniques, setTechniques] = useState([])
  const [promotionRequests, setPromotionRequests] = useState([])
  const [transferRequests, setTransferRequests] = useState([])
  const [correctionRequests, setCorrectionRequests] = useState([])
  const [competitionRequests, setCompetitionRequests] = useState([])
  const [pendingApprovals, setPendingApprovals] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [plans, setPlans] = useState([])
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [showAdd, setShowAdd] = useState(false)
  const [selectedFighter, setSelectedFighter] = useState(null)
  const [sharingFighter, setSharingFighter] = useState(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('list')

  const load = useCallback(async () => {
    if (!club) { setLoading(false); return }
    const [f, s, c, cats, techs, reqs, transfers, corrections, compReqs, pending, subs, plns] = await Promise.all([
      supabase.from('fighters').select('*').eq('club_id', club.id).order('created_at'),
      supabase.from('fighter_skills').select('*'),
      supabase.from('competitions').select('*'),
      supabase.from('skill_categories').select('*').eq('club_id', club.id).order('display_order'),
      supabase.from('techniques').select('*').order('display_order'),
      supabase.from('promotion_requests').select('*, fighters(*)').eq('club_id', club.id).eq('status', 'pending').order('requested_at', { ascending: false }),
      supabase.from('gym_transfers').select('*, fighters(*)').eq('to_club_id', club.id).eq('status', 'pending').order('requested_at', { ascending: false }),
      supabase.from('correction_requests').select('*, fighters(*)').eq('club_id', club.id).eq('status', 'pending').order('requested_at', { ascending: false }),
      supabase.from('competition_requests').select('*, fighters(*)').eq('club_id', club.id).eq('status', 'pending').order('requested_at', { ascending: false }),
      supabase.from('fighters').select('*').eq('club_id', club.id).eq('pending_approval', true).order('created_at', { ascending: false }),
      supabase.from('member_subscriptions').select('*'),
      supabase.from('membership_plans').select('*').eq('club_id', club.id).eq('active', true),
    ])
    setFighters(f.data || [])
    setSkills(s.data || [])
    setCompetitions(c.data || [])
    setCategories(cats.data || [])
    setTechniques(techs.data || [])
    setPromotionRequests(reqs.data || [])
    setTransferRequests(transfers.data || [])
    setCorrectionRequests(corrections.data || [])
    setCompetitionRequests(compReqs.data || [])
    setPendingApprovals(pending.data || [])
    setSubscriptions(subs.data || [])
    setPlans(plns.data || [])
    setLoading(false)
  }, [club])

  useEffect(() => { load() }, [load])

  const filtered = fighters.filter(f => {
    if (f.pending_approval) return false
    if (typeFilter !== 'all' && f.type !== typeFilter) return false
    if (search && !(f.first_name + ' ' + f.last_name).toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  if (loading) return <PageLoader />

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search fighters..."
          style={{ flex: 1, maxWidth: 220, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
        />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, background: '#fff' }}>
          <option value="all">All fighters</option>
          <option value="kids">Kids</option>
          <option value="adult">Adults</option>
        </select>
        <div style={{ display: 'flex', background: '#f0f0f0', borderRadius: 8, padding: 2, gap: 2 }}>
          <button onClick={() => setView('list')} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: view === 'list' ? '#fff' : 'transparent', fontSize: 13, fontWeight: view === 'list' ? 600 : 400, color: view === 'list' ? '#1860a8' : '#888', boxShadow: view === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', fontFamily: 'inherit' }}>☰ List</button>
          <button onClick={() => setView('cards')} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: view === 'cards' ? '#fff' : 'transparent', fontSize: 13, fontWeight: view === 'cards' ? 600 : 400, color: view === 'cards' ? '#1860a8' : '#888', boxShadow: view === 'cards' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', fontFamily: 'inherit' }}>⊞ Cards</button>
        </div>
        <button onClick={() => setShowAdd(true)} style={btnStyle}>+ Add fighter</button>
      </div>

      {pendingApprovals.length > 0 && (
        <div style={{ background: '#f0f8ff', border: '1px solid #90c8f8', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0c4480', marginBottom: 10 }}>
            New member requests ({pendingApprovals.length}) — verify belt rank
          </div>
          {pendingApprovals.map(f => {
            const belts = f.type === 'kids' ? KIDS_BELTS : ADULT_BELTS
            const belt = belts[f.belt_index] || belts[0]
            return (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #c0d8f0' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{f.first_name} {f.last_name}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>Claimed belt: <strong>{belt.n}</strong> · {f.type}</div>
                </div>
                <button onClick={async () => { await supabase.from('fighters').update({ pending_approval: false }).eq('id', f.id); load() }}
                  style={{ background: '#1a7040', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Approve</button>
                <button onClick={() => setSelectedFighter(f)}
                  style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Edit belt</button>
                <button onClick={async () => { if (window.confirm(`Decline ${f.first_name}'s request?`)) { await supabase.from('fighters').delete().eq('id', f.id); load() } }}
                  style={{ background: 'transparent', color: '#c00', border: '1px solid #fcc', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Decline</button>
              </div>
            )
          })}
        </div>
      )}

      {promotionRequests.length > 0 && (
        <div style={{ background: '#fff8e8', border: '1px solid #f8d88a', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#a06000', marginBottom: 10 }}>Promotion requests ({promotionRequests.length})</div>
          {promotionRequests.map(req => {
            const f = req.fighters
            const belts = f?.type === 'kids' ? KIDS_BELTS : ADULT_BELTS
            const currentBelt = belts[req.current_belt_index]?.n || 'Unknown'
            const nextBelt = belts[req.current_belt_index + 1]?.n || '—'
            return (
              <div key={req.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f8e8c0' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{f?.first_name} {f?.last_name}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{currentBelt} → {nextBelt}</div>
                </div>
                <button onClick={() => { const fighter = fighters.find(x => x.id === req.fighter_id); if (fighter) setSelectedFighter({ ...fighter, pendingPromotion: true }) }}
                  style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Review</button>
                <button onClick={async () => { await supabase.from('promotion_requests').update({ status: 'declined' }).eq('id', req.id); load() }}
                  style={{ background: 'transparent', color: '#c00', border: '1px solid #fcc', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Decline</button>
              </div>
            )
          })}
        </div>
      )}

      {transferRequests.length > 0 && (
        <div style={{ background: '#f0f8ff', border: '1px solid #b0d8f8', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0c4480', marginBottom: 10 }}>Transfer requests ({transferRequests.length})</div>
          {transferRequests.map(req => {
            const f = req.fighters
            return (
              <div key={req.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #c8e8f8' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{f?.first_name} {f?.last_name}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{f?.sessions_count || 0} sessions</div>
                </div>
                <button onClick={async () => {
                  await supabase.from('fighters').update({ club_id: club.id }).eq('id', req.fighter_id)
                  await supabase.from('fighter_gym_history').update({ is_current: false, left_at: new Date().toISOString() }).eq('fighter_id', req.fighter_id).eq('is_current', true)
                  await supabase.from('fighter_gym_history').insert({ fighter_id: req.fighter_id, club_id: club.id, is_current: true })
                  await supabase.from('gym_transfers').update({ status: 'approved', reviewed_at: new Date().toISOString() }).eq('id', req.id)
                  load()
                }} style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Approve</button>
                <button onClick={async () => { await supabase.from('gym_transfers').update({ status: 'declined' }).eq('id', req.id); load() }}
                  style={{ background: 'transparent', color: '#c00', border: '1px solid #fcc', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Decline</button>
              </div>
            )
          })}
        </div>
      )}

      {correctionRequests.length > 0 && (
        <div style={{ background: '#faf0ff', border: '1px solid #d8b8f8', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#5040b8', marginBottom: 10 }}>Correction requests ({correctionRequests.length})</div>
          {correctionRequests.map(req => (
            <div key={req.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: '1px solid #e8d0f8' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{req.fighters?.first_name} {req.fighters?.last_name}</div>
                <div style={{ fontSize: 12, color: '#444', marginTop: 3, fontStyle: 'italic' }}>"{req.description}"</div>
              </div>
              <button onClick={async () => { await supabase.from('correction_requests').update({ status: 'approved' }).eq('id', req.id); load() }}
                style={{ background: '#5040b8', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Resolved</button>
              <button onClick={async () => { await supabase.from('correction_requests').update({ status: 'declined' }).eq('id', req.id); load() }}
                style={{ background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: 7, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Dismiss</button>
            </div>
          ))}
        </div>
      )}


      {competitionRequests.length > 0 && (
        <div style={{ background: '#fff8e8', border: '1px solid #f8d88a', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#a06000', marginBottom: 10 }}>Competition results to review ({competitionRequests.length})</div>
          {competitionRequests.map(req => {
            const medalLabel = { gold: '🥇 Gold', silver: '🥈 Silver', bronze: '🥉 Bronze' }[req.medal] || 'No medal'
            return (
              <div key={req.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid #f8e8c0' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{req.fighters?.first_name} {req.fighters?.last_name}</div>
                  <div style={{ fontSize: 12, color: '#444', marginTop: 2 }}>{req.tournament_name} · {medalLabel}{req.wins ? ` · ${req.wins} wins` : ''}{req.submission_used ? ` · via ${req.submission_used}` : ''}</div>
                  {req.notes && <div style={{ fontSize: 11, color: '#888', marginTop: 3, fontStyle: 'italic' }}>"{req.notes}"</div>}
                </div>
                <button onClick={async () => {
                  await supabase.from('competitions').insert({
                    fighter_id: req.fighter_id,
                    competition_date: req.competition_date,
                    tournament_name: req.tournament_name,
                    medal: req.medal,
                    wins: req.wins,
                    submission_used: req.submission_used,
                  })
                  if (req.medal) {
                    await onCompetitionResult(req.fighter_id, club.id, req.medal, req.tournament_name)
                  }
                  await supabase.from('competition_requests').update({ status: 'approved', reviewed_at: new Date().toISOString() }).eq('id', req.id)
                  load()
                }} style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Approve</button>
                <button onClick={async () => { await supabase.from('competition_requests').update({ status: 'declined' }).eq('id', req.id); load() }}
                  style={{ background: 'transparent', color: '#c00', border: '1px solid #fcc', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Decline</button>
              </div>
            )
          })}
        </div>
      )}

      {filtered.length === 0
        ? <EmptyState onAdd={() => setShowAdd(true)} />
        : view === 'cards'
          ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
              {filtered.map(f => (
                <FighterCard key={f.id} fighter={f}
                  skills={skills.filter(s => s.fighter_id === f.id)}
                  competitions={competitions} categories={categories} techniques={techniques}
                  onClick={() => setSelectedFighter(f)} />
              ))}
            </div>
          : <RosterList fighters={filtered} subscriptions={subscriptions} plans={plans} onSelect={f => setSelectedFighter(f)} />
      }

      {showAdd && (
        <AddFighterModal clubId={club.id} categories={categories} techniques={techniques}
          onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load() }} />
      )}
      {selectedFighter && (
        <FighterEditModal fighter={selectedFighter} plans={plans} subscriptions={subscriptions}
          onClose={() => setSelectedFighter(null)}
          onSaved={() => { setSelectedFighter(null); load() }}
          onShare={(f) => { setSelectedFighter(null); setSharingFighter(f) }} />
      )}
      {sharingFighter && (
        <ShareCard fighter={sharingFighter} skills={skills.filter(s => s.fighter_id === sharingFighter.id)}
          competitions={competitions} categories={categories} techniques={techniques}
          onClose={() => setSharingFighter(null)} />
      )}
    </div>
  )
}

function RosterList({ fighters, subscriptions, plans, onSelect }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #eee', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 0.6fr 0.6fr 0.7fr 1fr 1fr 1fr 60px', padding: '10px 16px', background: '#f8f9fc', borderBottom: '1px solid #eee', fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>
        <div>Fighter</div><div>Belt</div><div>OVR</div><div>Sessions</div><div>Type</div><div>Phone</div><div>Date of Birth</div><div>Membership</div><div></div>
      </div>
      {fighters.map((f, i) => {
        const belts = f.type === 'kids' ? KIDS_BELTS : ADULT_BELTS
        const belt = belts[f.belt_index] || belts[0]
        let dobDisplay = '—'
        if (f.date_of_birth) {
          const d = new Date(f.date_of_birth)
          if (isNaN(d) === false) dobDisplay = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        }
        const sub = subscriptions.find(s => s.fighter_id === f.id)
        const membershipStatus = sub ? sub.status : 'none'
        const plan = sub ? plans.find(p => p.id === sub.plan_id) : null
        return (
          <div key={f.id} onClick={() => onSelect(f)}
            style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 0.6fr 0.6fr 0.7fr 1fr 1fr 1fr 60px', padding: '12px 16px', borderBottom: i < fighters.length - 1 ? '1px solid #f0f0f0' : 'none', cursor: 'pointer', background: 'white', transition: 'background 0.1s', alignItems: 'center' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8f9fc'}
            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#e8eef8', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {f.photo_url ? <img src={f.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 14 }}>👤</span>}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{f.first_name} {f.last_name}</div>
                <div style={{ fontSize: 11, color: '#aaa' }}>{f.email || '—'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: belt.color || '#ddd', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#444' }}>{belt.n}</span>
            </div>
            <div><span style={{ fontSize: 15, fontWeight: 700, color: '#1860a8' }}>{f.ovr_max || 0}</span></div>
            <div><span style={{ fontSize: 13, color: '#555' }}>{f.sessions_count || 0}</span></div>
            <div><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: f.type === 'kids' ? '#fef3c7' : '#e8f4ff', color: f.type === 'kids' ? '#92400e' : '#1860a8', fontWeight: 500 }}>{f.type === 'kids' ? 'Kids' : 'Adult'}</span></div>
            <div style={{ fontSize: 12, color: '#555' }}>{f.phone || '—'}</div>
            <div style={{ fontSize: 12, color: '#555' }}>{dobDisplay}</div>
            <div>
              <MembershipBadge status={membershipStatus} />
              {plan && <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{plan.name}</div>}
              {sub?.next_billing_date && sub.status === 'active' && (
                <div style={{ fontSize: 10, color: '#1a7040', marginTop: 1 }}>until {new Date(sub.next_billing_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}</div>
              )}
              {sub?.next_billing_date && sub.status === 'overdue' && (
                <div style={{ fontSize: 10, color: '#c00', marginTop: 1 }}>overdue {new Date(sub.next_billing_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</div>
              )}
              {sub?.paused_until && (
                <div style={{ fontSize: 10, color: '#1860a8', marginTop: 1 }}>resumes {new Date(sub.paused_until).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 11, color: '#1860a8', fontWeight: 500 }}>Edit →</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function MembershipSection({ fighter, plans, subscriptions, onSaved }) {
  const existing = subscriptions.find(s => s.fighter_id === fighter.id)
  const [planId, setPlanId] = useState(existing?.plan_id || '')
  const [status, setStatus] = useState(existing?.status || 'active')
  const [startDate, setStartDate] = useState(existing?.start_date || new Date().toISOString().split('T')[0])
  const [nextBilling, setNextBilling] = useState(existing?.next_billing_date || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  async function save() {
    if (!planId) return
    setSaving(true)
    if (existing) {
      await supabase.from('member_subscriptions').update({ plan_id: planId, status, start_date: startDate, next_billing_date: nextBilling || null, updated_at: new Date().toISOString() }).eq('id', existing.id)
    } else {
      await supabase.from('member_subscriptions').insert({ fighter_id: fighter.id, plan_id: planId, status, start_date: startDate, next_billing_date: nextBilling || null })
    }
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2000)
    onSaved()
  }

  async function cancelMembership() {
    if (!existing) return
    if (!window.confirm('Cancel this membership?')) return
    await supabase.from('member_subscriptions').update({ status: 'cancelled', cancelled_at: new Date().toISOString() }).eq('id', existing.id)
    onSaved()
  }

  return (
    <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1.25rem', marginBottom: '1.25rem' }}>
      <div style={{ fontSize: 11, color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 12 }}>Membership</div>
      {existing && (
        <div style={{ marginBottom: 10 }}>
          <MembershipBadge status={existing.status} />
          {existing.next_billing_date && <span style={{ fontSize: 11, color: '#aaa', marginLeft: 8 }}>Next billing: {new Date(existing.next_billing_date).toLocaleDateString('en-GB')}</span>}
        </div>
      )}
      <Grid>
        <FormField label="Plan">
          <select value={planId} onChange={e => setPlanId(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
            <option value="">— No plan —</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name} · {(p.amount / 100).toFixed(2)} {p.currency?.toUpperCase()} / {p.interval}</option>)}
          </select>
        </FormField>
        <FormField label="Status">
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
            <option value="active">Active</option>
            <option value="overdue">Overdue</option>
            <option value="frozen">Frozen</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </FormField>
        <FormField label="Start date">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
        </FormField>
        <FormField label="Next billing date">
          <input type="date" value={nextBilling} onChange={e => setNextBilling(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
        </FormField>
      </Grid>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving || !planId} style={{ ...btnStyle, fontSize: 12, padding: '6px 14px' }}>{saved ? '✓ Saved' : saving ? 'Saving...' : existing ? 'Update membership' : 'Assign plan'}</button>
        {existing && existing.status !== 'cancelled' && (
          <button onClick={cancelMembership} style={{ ...btnSecStyle, fontSize: 12, padding: '6px 14px', color: '#c00', borderColor: '#fcc' }}>Cancel membership</button>
        )}
      </div>
    </div>
  )
}

function AddFighterModal({ clubId, categories, techniques, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', type: 'kids', belt_index: 0,
    country: 'HR', avatar_style: 'avatar', avatar_seed: Math.floor(Math.random() * 100),
    pin_code: '', parent_email: '', parent_name: '',
    email: '', phone: '', date_of_birth: '', address: '',
    emergency_contact: '', emergency_phone: '', medical_notes: '',
    membership_start_date: ''
  })
  const [beltSessions, setBeltSessions] = useState(0)
  const [startingOvr, setStartingOvr] = useState(null)
  const [techBaseline, setTechBaseline] = useState(0)
  const [saving, setSaving] = useState(false)

  const belts = form.type === 'kids' ? KIDS_BELTS : ADULT_BELTS
  const currentBelt = belts[form.belt_index] || belts[0]
  const isFirstBelt = form.belt_index === 0
  const ovrFloor = currentBelt.ovrFloor
  const ovrThreshold = currentBelt.ovrThreshold

  const previewOvr = useMemo(() => {
    const level = calcBeltLevel(currentBelt, beltSessions)
    const levelProgress = calcLevelProgress(currentBelt, beltSessions)
    const levelComponent = ((level - 1) + levelProgress) / 10
    const techComponent = techBaseline / 10
    const raw = levelComponent * 0.60 + techComponent * 0.30
    return Math.round(ovrFloor + raw * (ovrThreshold - ovrFloor))
  }, [beltSessions, techBaseline, currentBelt, ovrFloor, ovrThreshold])

  const finalOvr = startingOvr !== null ? parseInt(startingOvr) : previewOvr
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  function generatePin() { set('pin_code', String(Math.floor(1000 + Math.random() * 9000))) }

  async function save() {
    if (!form.first_name || !form.last_name) return
    setSaving(true)
    const { data: fighter, error } = await supabase.from('fighters').insert({
      ...form, club_id: clubId, sessions_count: beltSessions, belt_sessions: beltSessions, ovr_max: finalOvr,
    }).select().single()
    if (!error && fighter) {
      const skillRows = techniques.map(t => ({ fighter_id: fighter.id, technique_id: t.id, stars: techBaseline }))
      if (skillRows.length) await supabase.from('fighter_skills').insert(skillRows)
      await supabase.from('fighter_gym_history').insert({ fighter_id: fighter.id, club_id: clubId, is_current: true })
    }
    setSaving(false)
    onSaved()
  }

  return (
    <Modal title="Add new fighter" onClose={onClose}>
      <Grid>
        <FormField label="First name"><input value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="Ivan" /></FormField>
        <FormField label="Last name"><input value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Horvat" /></FormField>
        <FormField label="Type">
          <select value={form.type} onChange={e => { set('type', e.target.value); set('belt_index', 0) }}>
            <option value="kids">Kids</option>
            <option value="adult">Adult</option>
          </select>
        </FormField>
        <FormField label="Belt">
          <select value={form.belt_index} onChange={e => { set('belt_index', parseInt(e.target.value)); setBeltSessions(0); setStartingOvr(null) }}>
            {belts.map((b, i) => <option key={i} value={i}>{b.n}</option>)}
          </select>
        </FormField>
        <FormField label="Country">
          <select value={form.country} onChange={e => set('country', e.target.value)}>
            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </FormField>
        <FormField label="Avatar style">
          <select value={form.avatar_style} onChange={e => set('avatar_style', e.target.value)}>
            <option value="avatar">Illustrated avatar</option>
            <option value="initials">Initials only</option>
          </select>
        </FormField>
      </Grid>
      {!isFirstBelt && (
        <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '14px', marginBottom: 12, border: '1px solid #c8d8f8' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#1860a8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>Existing belt holder</div>
          <Grid>
            <FormField label={`Sessions at ${currentBelt.n} belt`}>
              <input type="number" min="0" value={beltSessions} onChange={e => { setBeltSessions(parseInt(e.target.value) || 0); setStartingOvr(null) }} placeholder="0" />
            </FormField>
            <FormField label="Technique baseline (0–10)">
              <select value={techBaseline} onChange={e => { setTechBaseline(parseInt(e.target.value)); setStartingOvr(null) }}>
                <option value={0}>0 — Build from scratch</option>
                <option value={2}>2 — Beginner</option>
                <option value={4}>4 — Developing</option>
                <option value={6}>6 — Solid</option>
                <option value={8}>8 — Advanced</option>
                <option value={10}>10 — Expert</option>
              </select>
            </FormField>
          </Grid>
          <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #e0eaff' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: '#888' }}>Starting OVR</div>
              <div style={{ fontSize: 11, color: '#aaa' }}>{currentBelt.n} range: {ovrFloor}–{ovrThreshold}</div>
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#1860a8' }}>{finalOvr}</div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 5 }}>Override OVR manually</div>
            <input type="number" min={ovrFloor} max={ovrThreshold} value={startingOvr !== null ? startingOvr : ''} onChange={e => setStartingOvr(e.target.value === '' ? null : e.target.value)} placeholder={`${ovrFloor}–${ovrThreshold}`} style={{ width: 80, padding: '6px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 14, textAlign: 'center' }} />
          </div>
        </div>
      )}
      <div style={{ background: '#f8f9fc', borderRadius: 10, padding: '12px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: '#666', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>Personal data</div>
        <Grid>
          <FormField label={`Email${form.type === 'adult' ? ' *' : ''}`}>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="ivan@example.com" />
          </FormField>
          <FormField label={`Phone${form.type === 'adult' ? ' *' : ''}`}>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+385 91 234 5678" />
          </FormField>
          <FormField label="Date of birth *">
            <input type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} />
          </FormField>
          <FormField label="Membership start">
            <input type="date" value={form.membership_start_date} onChange={e => set('membership_start_date', e.target.value)} />
          </FormField>
        </Grid>
        <FormField label="Address">
          <input value={form.address} onChange={e => set('address', e.target.value)} placeholder="Ilica 1, Zagreb" />
        </FormField>
        <Grid>
          <FormField label="Emergency contact">
            <input value={form.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} placeholder="Ana Horvat" />
          </FormField>
          <FormField label="Emergency phone">
            <input value={form.emergency_phone} onChange={e => set('emergency_phone', e.target.value)} placeholder="+385 91 000 0000" />
          </FormField>
        </Grid>
        <FormField label="Medical notes">
          <textarea value={form.medical_notes} onChange={e => set('medical_notes', e.target.value)} placeholder="No known allergies" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, minHeight: 50, resize: 'vertical', fontFamily: 'inherit' }} />
        </FormField>
      </div>
      <div style={{ background: '#f8f9fc', borderRadius: 10, padding: '12px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: '#666', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>Login options</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
          <FormField label="PIN code">
            <input value={form.pin_code} onChange={e => set('pin_code', e.target.value.slice(0, 4))} placeholder="4-digit PIN" maxLength={4} style={{ letterSpacing: '0.3em', fontSize: 18 }} />
          </FormField>
          <button onClick={generatePin} style={{ ...btnSecStyle, whiteSpace: 'nowrap', marginBottom: 1 }}>Generate</button>
        </div>
        {form.pin_code && <div style={{ fontSize: 11, color: '#1860a8', marginBottom: 8 }}>PIN: {form.pin_code}</div>}
        <Grid>
          <FormField label="Parent name"><input value={form.parent_name} onChange={e => set('parent_name', e.target.value)} placeholder="Ana Horvat" /></FormField>
          <FormField label="Parent email"><input type="email" value={form.parent_email} onChange={e => set('parent_email', e.target.value)} placeholder="ana@example.com" /></FormField>
        </Grid>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={save} disabled={saving} style={btnStyle}>{saving ? 'Saving...' : 'Add fighter'}</button>
        <button onClick={onClose} style={btnSecStyle}>Cancel</button>
      </div>
    </Modal>
  )
}

function EmptyState({ onAdd }) {
  return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🥋</div>
      <h3 style={{ color: '#333', marginBottom: 8 }}>No fighters yet</h3>
      <p style={{ color: '#888', marginBottom: 20, fontSize: 14 }}>Add your first fighter to get started</p>
      <button onClick={onAdd} style={btnStyle}>+ Add fighter</button>
    </div>
  )
}

function PageLoader() {
  return <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>Loading...</div>
}

export function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: '1.5rem', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ flex: 1, margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#888', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

export function Grid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>{children}</div>
}

function PersonalDataSection({ fighter }) {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState({
    email: fighter.email || '', phone: fighter.phone || '',
    date_of_birth: fighter.date_of_birth || '', membership_start_date: fighter.membership_start_date || '',
    address: fighter.address || '', emergency_contact: fighter.emergency_contact || '',
    emergency_phone: fighter.emergency_phone || '', medical_notes: fighter.medical_notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const set = (k, v) => setData(d => ({ ...d, [k]: v }))

  async function save() {
    setSaving(true)
    await supabase.from('fighters').update(data).eq('id', fighter.id)
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1.25rem', marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: open ? 12 : 0 }}>
        <div style={{ fontSize: 11, color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.04em', flex: 1 }}>Personal data</div>
        <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: '#666' }}>{open ? 'Close' : 'Edit'}</button>
      </div>
      {!open && <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>{fighter.email || fighter.phone || fighter.date_of_birth ? [fighter.email, fighter.phone, fighter.date_of_birth].filter(Boolean).join(' · ') : 'No personal data saved yet'}</div>}
      {open && (
        <div>
          <Grid>
            <FormField label="Email"><input value={data.email} onChange={e => set('email', e.target.value)} type="email" placeholder="ivan@example.com" /></FormField>
            <FormField label="Phone"><input value={data.phone} onChange={e => set('phone', e.target.value)} placeholder="+385 91 234 5678" /></FormField>
            <FormField label="Date of birth"><input value={data.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} type="date" /></FormField>
            <FormField label="Membership start"><input value={data.membership_start_date} onChange={e => set('membership_start_date', e.target.value)} type="date" /></FormField>
          </Grid>
          <FormField label="Address"><input value={data.address} onChange={e => set('address', e.target.value)} placeholder="Ilica 1, Zagreb" /></FormField>
          <Grid>
            <FormField label="Emergency contact"><input value={data.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} placeholder="Ana Horvat" /></FormField>
            <FormField label="Emergency phone"><input value={data.emergency_phone} onChange={e => set('emergency_phone', e.target.value)} placeholder="+385 91 000 0000" /></FormField>
          </Grid>
          <FormField label="Medical notes">
            <textarea value={data.medical_notes} onChange={e => set('medical_notes', e.target.value)} placeholder="No known allergies" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }} />
          </FormField>
          <button onClick={save} disabled={saving} style={{ ...btnStyle, fontSize: 12, padding: '6px 14px' }}>{saved ? '✓ Saved' : saving ? 'Saving...' : 'Save personal data'}</button>
        </div>
      )}
    </div>
  )
}


function PaymentSection({ fighter, plans, subscriptions }) {
  const sub = subscriptions.find(s => s.fighter_id === fighter.id)
  const plan = sub ? plans.find(p => p.id === sub.plan_id) : null
  const [payments, setPayments] = useState([])
  const [loadedPayments, setLoadedPayments] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    amount: plan ? (plan.amount / 100).toFixed(2) : '',
    method: 'cash',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  })

  useEffect(() => {
    supabase.from('payment_history').select('*')
      .eq('fighter_id', fighter.id)
      .order('paid_at', { ascending: false })
      .limit(10)
      .then(({ data }) => { setPayments(data || []); setLoadedPayments(true) })
  }, [fighter.id])

  async function savePayment() {
    if (!form.amount) return
    setSaving(true)
    const amount = Math.round(parseFloat(form.amount) * 100)
    await supabase.from('payment_history').insert({
      fighter_id: fighter.id,
      subscription_id: sub?.id || null,
      plan_id: sub?.plan_id || null,
      amount,
      currency: plan?.currency || 'eur',
      status: 'succeeded',
      paid_at: new Date(form.date).toISOString(),
      manual: true,
      notes: `${form.method === 'cash' ? 'Cash' : 'Bank transfer'}${form.notes ? ' — ' + form.notes : ''}`,
    })
    if (sub && ['overdue','suspended','pending'].includes(sub.status)) {
      await supabase.from('member_subscriptions').update({
        status: 'active', overdue_since: null, payment_failed_count: 0,
        suspended_at: null, updated_at: new Date().toISOString()
      }).eq('id', sub.id)
    }
    const { data } = await supabase.from('payment_history').select('*')
      .eq('fighter_id', fighter.id).order('paid_at', { ascending: false }).limit(10)
    setPayments(data || [])
    setShowForm(false)
    setSaving(false)
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>Payment history</div>
        <button onClick={() => { setForm({ amount: plan ? (plan.amount/100).toFixed(2) : '', method: 'cash', date: new Date().toISOString().split('T')[0], notes: '' }); setShowForm(s => !s) }}
          style={{ background: '#1a7040', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
          + Record payment
        </button>
      </div>

      {showForm && (
        <div style={{ background: '#f0f6ff', borderRadius: 8, padding: '12px', marginBottom: 12, border: '1px solid #c8d8f8' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ flex: '1 1 80px' }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Amount ({plan?.currency?.toUpperCase() || 'EUR'})</div>
              <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00" step="0.01" min="0"
                style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #c8d8f8', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: '1 1 100px' }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Method</div>
              <select value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #c8d8f8', fontSize: 13, background: '#fff' }}>
                <option value="cash">Cash</option>
                <option value="bank">Bank transfer</option>
              </select>
            </div>
            <div style={{ flex: '1 1 110px' }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Date</div>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #c8d8f8', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Notes (optional)</div>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. 3 months paid in advance"
              style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #c8d8f8', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={savePayment} disabled={saving || !form.amount}
              style={{ background: '#1a7040', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              {saving ? 'Saving...' : 'Record payment'}
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: 7, padding: '7px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {!loadedPayments && <div style={{ fontSize: 12, color: '#aaa' }}>Loading...</div>}
      {loadedPayments && payments.length === 0 && <div style={{ fontSize: 12, color: '#aaa' }}>No payments recorded yet.</div>}
      {payments.map(p => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f4f4f4' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1a7040', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#1a1a1a' }}>{p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</div>
            {p.notes && <div style={{ fontSize: 11, color: '#888' }}>{p.notes}</div>}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a7040' }}>{(p.amount / 100).toFixed(2)} {p.currency?.toUpperCase()}</div>
        </div>
      ))}
    </div>
  )
}


function BadgesSection({ fighter, club }) {
  const [badges, setBadges] = useState([])
  const [points, setPoints] = useState(0)
  const [awarding, setAwarding] = useState(false)
  const [selectedBadge, setSelectedBadge] = useState('')

  useEffect(() => {
    supabase.from('fighter_badges').select('*')
      .eq('fighter_id', fighter.id)
      .order('awarded_at', { ascending: false })
      .then(({ data }) => setBadges(data || []))
    getFighterPoints(fighter.id).then(setPoints)
  }, [fighter.id])

  async function awardCoachBadge() {
    if (!selectedBadge) return
    setAwarding(true)
    const b = BADGES[selectedBadge]
    await supabase.from('fighter_badges').insert({
      fighter_id: fighter.id,
      club_id: club.id,
      badge_key: selectedBadge,
      badge_name: b.name,
      badge_icon: b.icon,
      auto: false,
    })
    const { data } = await supabase.from('fighter_badges').select('*')
      .eq('fighter_id', fighter.id).order('awarded_at', { ascending: false })
    setBadges(data || [])
    setSelectedBadge('')
    setAwarding(false)
  }

  async function removeBadge(id) {
    await supabase.from('fighter_badges').delete().eq('id', id)
    setBadges(b => b.filter(x => x.id !== id))
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
          Badges & Points
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1860a8' }}>⚡ {points} pts</div>
      </div>

      {/* Badges grid */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {badges.length === 0 && <div style={{ fontSize: 12, color: '#aaa' }}>No badges yet.</div>}
        {badges.map(b => (
          <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f0f6ff', borderRadius: 20, padding: '4px 10px', border: '1px solid #c8d8f8', position: 'relative' }}>
            <span style={{ fontSize: 16 }}>{b.badge_icon}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#1860a8' }}>{b.badge_name}</span>
            {!b.auto && (
              <button onClick={() => removeBadge(b.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: '0 0 0 2px', lineHeight: 1 }}>×</button>
            )}
          </div>
        ))}
      </div>

      {/* Award coach badge */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select value={selectedBadge} onChange={e => setSelectedBadge(e.target.value)}
          style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid #ddd', fontSize: 12, background: '#fff' }}>
          <option value="">Award a badge...</option>
          {COACH_BADGES.map(key => (
            <option key={key} value={key}>{BADGES[key].icon} {BADGES[key].name}</option>
          ))}
        </select>
        <button onClick={awardCoachBadge} disabled={!selectedBadge || awarding}
          style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          {awarding ? '...' : 'Award'}
        </button>
      </div>
    </div>
  )
}

function FighterEditModal({ fighter, plans, subscriptions, onClose, onSaved, onShare }) {
  const belts = fighter.type === 'kids' ? KIDS_BELTS : ADULT_BELTS
  const [beltIndex, setBeltIndex] = useState(fighter.belt_index)
  const currentOvr = fighter.ovr_max || 0
  const [saving, setSaving] = useState(false)
  const [showPromotion, setShowPromotion] = useState(false)
  const [promotedIndex, setPromotedIndex] = useState(null)

  function promote() { if (beltIndex >= belts.length - 1) return; setBeltIndex(i => i + 1) }

  async function save() {
    setSaving(true)
    const b = fighter.type === 'kids' ? KIDS_BELTS : ADULT_BELTS
    await supabase.from('fighters').update({ belt_index: beltIndex, belt_sessions: 0, ovr_max: Math.max(fighter.ovr_max || 0, currentOvr) }).eq('id', fighter.id)
    if (beltIndex > fighter.belt_index) {
      const newBeltName = b[beltIndex]?.n || 'new belt'
      await supabase.from('notifications').insert({ fighter_id: fighter.id, club_id: fighter.club_id, type: 'belt_promotion', title: 'Belt promotion!', body: `${fighter.first_name} has been promoted to ${newBeltName} belt!` })
      setPromotedIndex(beltIndex)
      setShowPromotion(true)
    } else { onSaved() }
    setSaving(false)
  }

  if (showPromotion) return <BeltPromotion fighter={{ ...fighter, belt_index: promotedIndex }} newBeltIndex={promotedIndex} onClose={() => { setShowPromotion(false); onSaved() }} />

  return (
    <Modal title={`${fighter.first_name} ${fighter.last_name}`} onClose={onClose}>
      <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ fontSize: 11, color: '#666', fontWeight: 500, marginBottom: 12 }}>FIGHTER PHOTO</div>
        <PhotoUpload fighter={fighter} onUploaded={onSaved} />
      </div>
      <PersonalDataSection fighter={fighter} />
      <MembershipSection fighter={fighter} plans={plans} subscriptions={subscriptions} onSaved={onSaved} />
      <PaymentSection fighter={fighter} plans={plans} subscriptions={subscriptions} />
      <BadgesSection fighter={fighter} club={club} />
      <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ fontSize: 11, color: '#666', fontWeight: 500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>PIN login</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '0.3em', color: '#1a1a1a', flex: 1 }}>{fighter.pin_code || '—'}</div>
          <button onClick={async () => { const newPin = String(Math.floor(1000 + Math.random() * 9000)); await supabase.from('fighters').update({ pin_code: newPin }).eq('id', fighter.id); onSaved() }} style={{ ...btnSecStyle, fontSize: 11 }}>{fighter.pin_code ? 'Reset PIN' : 'Generate PIN'}</button>
        </div>
        {fighter.parent_name && <div style={{ fontSize: 12, color: '#444', marginTop: 6 }}>Parent: {fighter.parent_name} {fighter.parent_email ? `· ${fighter.parent_email}` : ''}</div>}
      </div>
      <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ fontSize: 11, color: '#666', fontWeight: 500, marginBottom: 12 }}>BELT RANK</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <select value={beltIndex} onChange={e => setBeltIndex(parseInt(e.target.value))} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
              {belts.map((b, i) => <option key={i} value={i}>{b.n}</option>)}
            </select>
          </div>
          {beltIndex < belts.length - 1 && (
            <button onClick={promote} style={{ background: '#eaf7f0', color: '#1a7040', border: '1px solid #b0e8cc', borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Promote →</button>
          )}
        </div>
        {beltIndex !== fighter.belt_index && <div style={{ fontSize: 12, color: '#1860a8', marginTop: 8 }}>Pending: {belts[fighter.belt_index]?.n} → {belts[beltIndex]?.n}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving} style={btnStyle}>{saving ? 'Saving...' : beltIndex > fighter.belt_index ? '🎉 Promote!' : 'Save changes'}</button>
        <button onClick={() => onShare(fighter)} style={{ ...btnSecStyle, background: '#f8f4ff', color: '#5040b8', borderColor: '#d0c8f8' }}>Share card</button>
        <button onClick={onClose} style={btnSecStyle}>Close</button>
        <button onClick={async () => { if (window.confirm(`Permanently delete ${fighter.first_name} ${fighter.last_name}?`)) { await supabase.from('fighters').delete().eq('id', fighter.id); onSaved() } }} style={{ ...btnSecStyle, color: '#c00', borderColor: '#fcc', marginLeft: 'auto' }}>Delete fighter</button>
      </div>
    </Modal>
  )
}

export function FormField({ label, children, fullWidth }) {
  return (
    <div style={fullWidth ? { gridColumn: '1/-1' } : {}}>
      <label style={{ display: 'block', fontSize: 11, color: '#666', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  )
}

export const btnStyle = { background: '#1860a8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit' }
export const btnSecStyle = { background: 'transparent', color: '#444', border: '1px solid #ddd', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }
